import { useEffect, useState, type FormEvent } from 'react';
import { FormField } from '../shared/form';
import { useUpdateModelProvider } from '../../hooks/useModels';
import type { ModelProvider, UpdateModelProviderInput } from '../../types';
import { CatalogDialog, PRIMARY_BUTTON, SECONDARY_BUTTON } from './CatalogDialog';
import { catalogFormErrors, type CatalogFormErrors } from './catalogErrors';
import { API_KEY_MIN_LENGTH } from './AddModelProviderDialog';

/**
 * Edit provider (task 1102): display name, base URL, and a replacement API key
 * through `PATCH /api/model-providers/:id` (service task 1101). Key and
 * protocol are immutable, so they are shown, not offered. The stored API key
 * never comes back from the service; the field starts empty and its placeholder
 * names the last four characters. Leaving it empty keeps the stored key; a new
 * key clears the provider's verify state on the service.
 */

const EMPTY_ERRORS: CatalogFormErrors = { fields: {}, general: null };

interface EditModelProviderDialogProps {
  provider: ModelProvider | null;
  onClose: () => void;
  /**
   * Open with the cursor in the API key field (task 1167): the Models page's
   * Add key, Replace and Replace with stored key all mean "type a key".
   */
  focusKey?: boolean;
}

export function EditModelProviderDialog({ provider, onClose, focusKey = false }: EditModelProviderDialogProps) {
  const update = useUpdateModelProvider();
  const open = provider !== null;
  const [displayName, setDisplayName] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [errors, setErrors] = useState<CatalogFormErrors>(EMPTY_ERRORS);

  useEffect(() => {
    if (!provider) return;
    setDisplayName(provider.displayName);
    setBaseUrl(provider.baseUrl ?? '');
    setApiKey('');
    setErrors(EMPTY_ERRORS);
    update.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider?.id]);

  const baseUrlRequired = provider?.adapter === 'openai-compatible';

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!provider) return;
    const fields: Record<string, string> = {};
    if (!displayName.trim()) fields.displayName = 'Display name is required';
    if (baseUrlRequired && !baseUrl.trim()) fields.baseUrl = 'Base URL is required for an OpenAI-compatible provider';
    if (apiKey && apiKey.trim().length < API_KEY_MIN_LENGTH) fields.apiKey = `API key must be at least ${API_KEY_MIN_LENGTH} characters`;
    if (Object.keys(fields).length > 0) {
      setErrors({ fields, general: null });
      return;
    }

    // Send only what changed.
    const data: UpdateModelProviderInput = {};
    if (displayName.trim() !== provider.displayName) data.displayName = displayName.trim();
    const nextBaseUrl = baseUrl.trim() || null;
    if (nextBaseUrl !== (provider.baseUrl ?? null)) data.baseUrl = nextBaseUrl;
    if (apiKey.trim()) data.apiKey = apiKey.trim();
    if (Object.keys(data).length === 0) {
      onClose();
      return;
    }

    setErrors(EMPTY_ERRORS);
    try {
      await update.mutateAsync({ id: provider.id, data });
      onClose();
    } catch (error) {
      setErrors(catalogFormErrors(error));
    }
  };

  const lastFour = provider?.apiKeyLastFour;

  return (
    <CatalogDialog
      open={open}
      onClose={onClose}
      busy={update.isPending}
      title={provider ? `Edit ${provider.displayName}` : 'Edit provider'}
      description={
        provider ? (
          <>
            Key <span className="font-mono">{provider.key}</span>
            {provider.adapter ? ` · ${provider.adapter}` : ''}. Key and protocol cannot change.
          </>
        ) : undefined
      }
      testId="edit-provider-dialog"
    >
      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <FormField
          label="Display name"
          name="edit-provider-display-name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          error={errors.fields.displayName}
          required
          autoFocus={!focusKey}
        />
        <FormField
          label="Base URL"
          name="edit-provider-base-url"
          type="url"
          value={baseUrl}
          onChange={(e) => setBaseUrl(e.target.value)}
          error={errors.fields.baseUrl}
          required={baseUrlRequired}
        />
        <div>
          <FormField
            label="API key"
            name="edit-provider-api-key"
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            error={errors.fields.apiKey}
            placeholder={lastFour ? `Last four: ${lastFour}` : 'No stored key'}
            autoFocus={focusKey}
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Leave empty to keep the stored key.</p>
        </div>

        {errors.general && (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">
            {errors.general}
          </p>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} disabled={update.isPending} className={SECONDARY_BUTTON}>
            Cancel
          </button>
          <button type="submit" disabled={update.isPending} className={PRIMARY_BUTTON}>
            {update.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </CatalogDialog>
  );
}
